import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useBranch } from "@/context/BranchContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtGHS, fmtDateTime } from "@/lib/format";
import { printDoc, esc } from "@/lib/print";
import { Printer, Search, Ban } from "lucide-react";
import { printReceipt } from "@/lib/receipt";

export default function Orders() {
  const { branchParam } = useBranch();
  const { can } = useAuth();
  const [orders, setOrders] = useState([]);
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");
  const [returns, setReturns] = useState([]);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const printInvoice = () => {
    if (!sel) return;
    const rows = sel.lines.map((l) => `<tr><td>${esc(l.generic_name)}${l.covered ? " (insured)" : ""}</td><td class="r">${l.quantity}</td><td class="r">${fmtGHS(l.unit_price)}</td><td class="r">${fmtGHS(l.line_total)}</td></tr>`).join("");
    const retRows = returns.length
      ? `<tr><td colspan="4" style="padding-top:10px;font-weight:bold;color:#8a1c1c">Returns / Credit notes</td></tr>`
        + returns.map((r) => `<tr><td>${r.return_no} · ${(r.reason || "").replace(/_/g, " ")}</td><td class="r"></td><td class="r"></td><td class="r" style="color:#8a1c1c">- ${fmtGHS(r.amount)}</td></tr>`).join("")
      : "";
    const returnedTotal = returns.reduce((s, r) => s + (r.amount || 0), 0);
    const body = `<div class="row"><div><img src="${window.location.origin}/mobimed-logo.png" alt="" style="height:46px;margin-bottom:6px"/><h1>MobiMed Pharmacy</h1><h2>Invoice ${sel.invoice_no}</h2><div class="muted">${esc(sel.branch_name)}</div></div>
      <div class="muted" style="text-align:right">${fmtDateTime(sel.created_at)}<br/>${esc(sel.customer_name)}${sel.corporate_member_name ? `<br/>Member: ${esc(sel.corporate_member_name)}${sel.corporate_member_staff_no ? ` (${esc(sel.corporate_member_staff_no)})` : ""}` : ""}<br/>${(sel.customer_type || "")} customer</div></div>
      <table><thead><tr><th>Item</th><th class="r">Qty</th><th class="r">Unit</th><th class="r">Total</th></tr></thead>
      <tbody>${rows}${retRows}</tbody>
      <tfoot>
        <tr><td colspan="3" class="r">Subtotal</td><td class="r">${fmtGHS(sel.subtotal)}</td></tr>
        <tr><td colspan="3" class="r">Tax</td><td class="r">${fmtGHS(sel.tax)}</td></tr>
        ${sel.delivery_fee > 0 ? `<tr><td colspan="3" class="r">Delivery fee</td><td class="r">${fmtGHS(sel.delivery_fee)}</td></tr>` : ""}
        ${sel.insurance_claim_amount > 0 ? `<tr><td colspan="3" class="r">Insurance claim</td><td class="r">${fmtGHS(sel.insurance_claim_amount)}</td></tr>` : ""}
        ${sel.on_account > 0 ? `<tr><td colspan="3" class="r">On account</td><td class="r">${fmtGHS(sel.on_account)}</td></tr>` : ""}
        <tr><td colspan="3" class="r">Total</td><td class="r">${fmtGHS(sel.total)}</td></tr>
        ${returnedTotal > 0 ? `<tr><td colspan="3" class="r">Less returns</td><td class="r">- ${fmtGHS(returnedTotal)}</td></tr>
        <tr><td colspan="3" class="r">Net after returns</td><td class="r">${fmtGHS(sel.total - returnedTotal)}</td></tr>` : ""}
      </tfoot></table>
      <div class="muted" style="margin-top:18px">Dispensed by ${esc(sel.dispensed_by)} · Thank you for choosing MobiMed</div>`;
    printDoc(`Invoice ${sel.invoice_no}`, body);
  };

  const load = () => api.get("/orders", { params: { branch_id: branchParam } }).then((r) => setOrders(r.data));

  const doCancel = async () => {
    if (!sel) return;
    setCancelling(true);
    try {
      await api.post(`/orders/${sel.id}/cancel`, { reason: cancelReason });
      toast.success(`${sel.invoice_no} voided — stock restored`);
      setConfirmCancel(false); setCancelReason(""); setSel(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setCancelling(false); }
  };

  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [branchParam]);

  useEffect(() => {
    if (!sel) { setReturns([]); return; }
    api.get("/sales-returns", { params: { order_id: sel.id } }).then((r) => setReturns(r.data)).catch(() => setReturns([]));
  }, [sel]);

  const s = q.trim().toLowerCase();
  const filtered = !s ? orders : orders.filter((o) =>
    (o.invoice_no || "").toLowerCase().includes(s) ||
    (o.customer_name || "").toLowerCase().includes(s) ||
    (o.branch_name || "").toLowerCase().includes(s));

  const returnedTotal = returns.reduce((sum, r) => sum + (r.amount || 0), 0);

  return (
    <div>
      <PageHeader title="Sales & Invoices" subtitle="Dispensing history across the selected branch scope" />

      <Card className="p-3 shadow-sm mb-4">
        <div className="relative max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
          <Input data-testid="invoice-search-input" placeholder="Search invoice no. or customer…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
      </Card>

      <Card className="shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr><th className="py-2.5 px-3">Invoice</th><th className="py-2.5 px-3">Branch</th><th className="py-2.5 px-3">Customer</th>
            <th className="py-2.5 px-3">Type</th><th className="py-2.5 px-3">Payment</th><th className="py-2.5 px-3 text-right">Total</th>
            <th className="py-2.5 px-3">Dispensed by</th><th className="py-2.5 px-3">When</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">{orders.length ? "No invoices match your search" : "No sales yet"}</td></tr>}
            {filtered.map((o) => (
              <tr key={o.id} className="border-t border-border hover:bg-muted/50 cursor-pointer" data-testid={`order-row-${o.invoice_no}`} onClick={() => setSel(o)}>
                <td className="py-2 px-3 font-mono text-xs text-sky-700">{o.invoice_no}{o.status === "cancelled" && <span className="ml-2 text-[10px] font-semibold uppercase rounded-full px-1.5 py-0.5 bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">Voided</span>}</td>
                <td className="py-2 px-3 text-muted-foreground">{o.branch_name}</td>
                <td className="py-2 px-3">{o.customer_name}{o.corporate_member_name && <div className="text-[11px] text-muted-foreground">{o.corporate_member_name}</div>}</td>
                <td className="py-2 px-3"><StatusBadge value={o.customer_type} /></td>
                <td className="py-2 px-3"><StatusBadge value={o.payment_status} label={o.payment_status.replace(/_/g, " ")} /></td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(o.total)}</td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{o.dispensed_by}</td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{fmtDateTime(o.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center justify-between gap-4"><span>{sel?.invoice_no}{sel?.status === "cancelled" && <span className="ml-2 align-middle text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">Voided</span>}</span>{sel && <div className="flex items-center gap-2">{can("sales.returns") && sel.status === "completed" && <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50 dark:hover:bg-rose-500/10" data-testid="void-sale-btn" onClick={() => setConfirmCancel(true)}><Ban className="h-3.5 w-3.5 mr-1.5" /> Void sale</Button>}<Button size="sm" variant="outline" data-testid="print-receipt-btn" onClick={() => printReceipt(sel)}><Printer className="h-3.5 w-3.5 mr-1.5" /> Receipt</Button><Button size="sm" variant="outline" data-testid="print-invoice-btn" onClick={printInvoice}><Printer className="h-3.5 w-3.5 mr-1.5" /> Print / Save PDF</Button></div>}</DialogTitle></DialogHeader>
          {sel && (
            <div className="text-sm">
              <div className="flex justify-between text-muted-foreground mb-3">
                <span>{sel.customer_name}{sel.corporate_member_name ? ` (${sel.corporate_member_name})` : ""} · {sel.branch_name}</span><StatusBadge value={sel.customer_type} />
              </div>
              <div className="divide-y divide-border border-y border-border">
                {sel.lines.map((l, i) => (
                  <div key={i} className="py-2 flex justify-between">
                    <div>
                      <div className="font-medium">{l.generic_name} × {l.quantity} {l.covered && <span className="text-sky-600 text-xs">(insured)</span>}</div>
                      <div className="text-xs text-muted-foreground font-mono">{(l.allocations || []).map((a) => `${a.batch_no}(${a.quantity})`).join(", ")}</div>
                    </div>
                    <span className="font-mono">{fmtGHS(l.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 space-y-1">
                <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="font-mono">{fmtGHS(sel.subtotal)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Tax</span><span className="font-mono">{fmtGHS(sel.tax)}</span></div>
                {sel.delivery_fee > 0 && <div className="flex justify-between text-muted-foreground" data-testid="order-delivery-fee"><span>Delivery fee</span><span className="font-mono">{fmtGHS(sel.delivery_fee)}</span></div>}
                {sel.insurance_claim_amount > 0 && <div className="flex justify-between text-sky-700"><span>Insurance claim</span><span className="font-mono">{fmtGHS(sel.insurance_claim_amount)}</span></div>}
                {sel.on_account > 0 && <div className="flex justify-between text-violet-700"><span>On account</span><span className="font-mono">{fmtGHS(sel.on_account)}</span></div>}
                <div className="flex justify-between font-semibold pt-2 border-t"><span>Total</span><span className="font-mono">{fmtGHS(sel.total)}</span></div>
              </div>

              {returns.length > 0 && (
                <div className="mt-4 rounded-lg border border-rose-200 dark:border-rose-500/30 bg-rose-50/60 dark:bg-rose-500/10 p-3" data-testid="invoice-returns-section">
                  <div className="text-xs font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-300 mb-2">Returns / Credit notes</div>
                  <div className="space-y-1">
                    {returns.map((r) => (
                      <div key={r.id} className="flex justify-between text-xs" data-testid={`invoice-return-${r.return_no}`}>
                        <span className="font-mono text-rose-700 dark:text-rose-300">{r.return_no}</span>
                        <span className="text-muted-foreground capitalize">{(r.reason || "").replace(/_/g, " ")} · {r.refund_method === "credit_note" ? "credit note" : r.refund_method}</span>
                        <span className="font-mono">- {fmtGHS(r.amount)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between font-semibold pt-2 mt-2 border-t border-rose-200 dark:border-rose-500/30">
                    <span>Net after returns</span><span className="font-mono" data-testid="invoice-net-after-returns">{fmtGHS(sel.total - returnedTotal)}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmCancel} onOpenChange={(o) => !o && setConfirmCancel(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Void {sel?.invoice_no}?</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">This reverses the sale: stock returns to its original batches, any corporate balance and insurance claim are reversed, and a linked delivery is cancelled. This cannot be undone.</div>
          <div className="mt-3"><Label className="text-xs">Reason</Label><Input data-testid="void-reason-input" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. wrong item dispensed" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCancel(false)} data-testid="void-cancel-btn">Keep sale</Button>
            <Button variant="destructive" disabled={cancelling} onClick={doCancel} data-testid="void-confirm-btn">{cancelling ? "Voiding…" : "Void sale"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
